import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Camera, ArrowLeft, Circle, Stop } from '@phosphor-icons/react';
import { EventDropLogo } from '../components/EventDropLogo';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EFFECTS = [
  { id: 'none', label: 'None', emoji: '📷' },
  { id: 'confetti', label: 'Confetti', emoji: '🎉' },
  { id: 'hearts', label: 'Hearts', emoji: '💖' },
  { id: 'neon', label: 'Neon', emoji: '🌈' },
  { id: 'disco', label: 'Disco', emoji: '🪩' },
  { id: 'sparkle', label: 'Sparkle', emoji: '✨' },
  { id: 'fire', label: 'Fire', emoji: '🔥' },
  { id: 'spotlight', label: 'Spotlight', emoji: '🔦' },
  { id: 'rainbow', label: 'Rainbow', emoji: '🌊' },
  { id: 'stars', label: 'Stars', emoji: '⭐' },
  { id: 'cinematic', label: 'Cinematic', emoji: '🎬' },
];

// Particle system
class Particle {
  constructor(type, w, h) {
    this.type = type;
    this.x = Math.random() * w;
    this.y = type === 'confetti' ? -10 : Math.random() * h;
    this.w = w; this.h = h;
    this.size = 4 + Math.random() * 8;
    this.speed = 1 + Math.random() * 3;
    this.angle = Math.random() * Math.PI * 2;
    this.spin = (Math.random() - 0.5) * 0.1;
    this.opacity = 0.5 + Math.random() * 0.5;
    this.color = ['#A033D8', '#FFD700', '#C29D52', '#B89762', '#26A69A', '#B89762', '#38BDF8'][Math.floor(Math.random() * 7)];
    this.life = 1;
  }
  update() {
    if (this.type === 'confetti') { this.y += this.speed * 2; this.x += Math.sin(this.angle) * 1.5; this.angle += this.spin; if (this.y > this.h) { this.y = -10; this.x = Math.random() * this.w; } }
    else if (this.type === 'hearts') { this.y -= this.speed; this.x += Math.sin(this.angle) * 0.5; this.angle += 0.02; this.opacity -= 0.003; if (this.y < -20 || this.opacity <= 0) { this.y = this.h + 10; this.x = Math.random() * this.w; this.opacity = 0.7; } }
    else if (this.type === 'sparkle') { this.opacity = 0.3 + Math.sin(Date.now() * 0.005 + this.x) * 0.5; this.size = 3 + Math.sin(Date.now() * 0.003 + this.y) * 4; }
    else if (this.type === 'stars') { this.y -= this.speed * 0.5; this.opacity = 0.4 + Math.sin(Date.now() * 0.004 + this.x) * 0.4; if (this.y < -10) { this.y = this.h + 10; this.x = Math.random() * this.w; } }
  }
  draw(ctx) {
    ctx.globalAlpha = this.opacity;
    if (this.type === 'confetti') {
      ctx.save(); ctx.translate(this.x, this.y); ctx.rotate(this.angle);
      ctx.fillStyle = this.color; ctx.fillRect(-this.size/2, -this.size/4, this.size, this.size/2);
      ctx.restore();
    } else if (this.type === 'hearts') {
      ctx.font = `${this.size * 2}px serif`; ctx.fillText('💖', this.x, this.y);
    } else if (this.type === 'sparkle' || this.type === 'stars') {
      ctx.fillStyle = this.color; ctx.beginPath(); ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
  }
}

const EffectsMode = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [activeEffect, setActiveEffect] = useState('none');
  const [recording, setRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const particlesRef = useRef([]);
  const animRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/events/slug/${slug}`); setEvent(r.data); } catch {}
    })();
    startCamera();
    return () => { stopCamera(); cancelAnimationFrame(animRef.current); clearInterval(timerRef.current); };
  }, [slug]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 720, height: 1280 }, audio: true });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
      setCameraReady(true);
      startEffectLoop();
    } catch {}
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };

  // Effect rendering loop
  const startEffectLoop = () => {
    const draw = () => {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      if (!canvas || !video) { animRef.current = requestAnimationFrame(draw); return; }
      const ctx = canvas.getContext('2d');
      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      // Draw particles
      particlesRef.current.forEach(p => { p.update(); p.draw(ctx); });

      // Overlay effects
      if (activeEffect === 'neon') {
        ctx.strokeStyle = `hsl(${(Date.now() * 0.1) % 360}, 100%, 60%)`;
        ctx.lineWidth = 4; ctx.shadowColor = ctx.strokeStyle; ctx.shadowBlur = 20;
        const r = 16; const m = 8;
        ctx.beginPath(); ctx.moveTo(m+r,m); ctx.lineTo(w-m-r,m); ctx.quadraticCurveTo(w-m,m,w-m,m+r); ctx.lineTo(w-m,h-m-r); ctx.quadraticCurveTo(w-m,h-m,w-m-r,h-m); ctx.lineTo(m+r,h-m); ctx.quadraticCurveTo(m,h-m,m,h-m-r); ctx.lineTo(m,m+r); ctx.quadraticCurveTo(m,m,m+r,m); ctx.closePath(); ctx.stroke();
        ctx.shadowBlur = 0;
      } else if (activeEffect === 'disco') {
        const hue = (Date.now() * 0.15) % 360;
        ctx.fillStyle = `hsla(${hue}, 80%, 50%, 0.08)`; ctx.fillRect(0, 0, w, h);
        for (let i = 0; i < 5; i++) {
          ctx.fillStyle = `hsla(${(hue + i * 60) % 360}, 90%, 60%, 0.15)`;
          const bx = (Math.sin(Date.now() * 0.002 + i) * 0.5 + 0.5) * w;
          const by = (Math.cos(Date.now() * 0.003 + i) * 0.5 + 0.5) * h;
          ctx.beginPath(); ctx.arc(bx, by, 60, 0, Math.PI * 2); ctx.fill();
        }
      } else if (activeEffect === 'fire') {
        const grad = ctx.createLinearGradient(0, h, 0, h - 80);
        grad.addColorStop(0, 'rgba(108,92,231,0.4)'); grad.addColorStop(0.5, 'rgba(62,198,231,0.15)'); grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad; ctx.fillRect(0, 0, w, h);
        const gradT = ctx.createLinearGradient(0, 0, 0, 80);
        gradT.addColorStop(0, 'rgba(108,92,231,0.3)'); gradT.addColorStop(1, 'transparent');
        ctx.fillStyle = gradT; ctx.fillRect(0, 0, w, 80);
      } else if (activeEffect === 'spotlight') {
        ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(0, 0, w, h);
        const cx = w/2, cy = h * 0.4;
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 150);
        grad.addColorStop(0, 'rgba(0,0,0,0)'); grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(cx, cy, 150, 0, Math.PI * 2); ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
      } else if (activeEffect === 'rainbow') {
        for (let i = 0; i < 5; i++) {
          const y = h * 0.2 + Math.sin(Date.now() * 0.002 + i * 0.5) * 30 + i * 20;
          ctx.strokeStyle = `hsla(${(Date.now() * 0.05 + i * 50) % 360}, 80%, 60%, 0.25)`;
          ctx.lineWidth = 8; ctx.beginPath(); ctx.moveTo(0, y);
          for (let x = 0; x < w; x += 10) { ctx.lineTo(x, y + Math.sin(x * 0.02 + Date.now() * 0.003) * 15); }
          ctx.stroke();
        }
      } else if (activeEffect === 'cinematic') {
        ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(0, 0, w, h * 0.12); ctx.fillRect(0, h * 0.88, w, h * 0.12);
        ctx.fillStyle = 'rgba(255,200,100,0.03)'; ctx.fillRect(0, 0, w, h);
      }

      animRef.current = requestAnimationFrame(draw);
    };
    draw();
  };

  // Update particles when effect changes
  useEffect(() => {
    const w = canvasRef.current?.width || 300;
    const h = canvasRef.current?.height || 500;
    if (['confetti', 'hearts', 'sparkle', 'stars'].includes(activeEffect)) {
      const count = activeEffect === 'confetti' ? 40 : activeEffect === 'hearts' ? 15 : 25;
      particlesRef.current = Array.from({ length: count }, () => new Particle(activeEffect, w, h));
    } else {
      particlesRef.current = [];
    }
  }, [activeEffect]);

  // Recording
  const startRec = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
    const rec = new MediaRecorder(streamRef.current, { mimeType: mime });
    recorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => { const blob = new Blob(chunksRef.current, { type: mime }); handleUpload(blob); };
    rec.start(100);
    setRecording(true); setRecordTime(0);
    timerRef.current = setInterval(() => {
      setRecordTime(prev => { if (prev >= 15) { stopRec(); return 15; } return prev + 1; });
    }, 1000);
  };

  const stopRec = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current?.state !== 'inactive') recorderRef.current.stop();
    setRecording(false);
  };

  const handleUpload = async (blob) => {
    if (!event) return;
    setUploading(true);
    try {
      const f = new File([blob], `effects_${activeEffect}_${Date.now()}.webm`, { type: 'video/webm' });
      const fd = new FormData(); fd.append('file', f);
      await axios.post(`${API}/events/${event.event_id}/photos?source=effects`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Memory saved with ' + activeEffect + ' effect!');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const takePhoto = () => {
    if (!videoRef.current || !event) return;
    const cv = document.createElement('canvas');
    const v = videoRef.current;
    cv.width = v.videoWidth || 720; cv.height = v.videoHeight || 1280;
    const ctx = cv.getContext('2d');
    ctx.save(); ctx.scale(-1, 1); ctx.drawImage(v, -cv.width, 0, cv.width, cv.height); ctx.restore();
    // Draw effect overlay
    if (canvasRef.current) { ctx.drawImage(canvasRef.current, 0, 0, cv.width, cv.height); }
    cv.toBlob(async (blob) => {
      if (!blob) return;
      setUploading(true);
      try {
        const f = new File([blob], `effects_${activeEffect}_${Date.now()}.jpg`, { type: 'image/jpeg' });
        const fd = new FormData(); fd.append('file', f);
        await axios.post(`${API}/events/${event.event_id}/photos?source=effects`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Photo saved!');
      } catch { toast.error('Upload failed'); }
      finally { setUploading(false); }
    }, 'image/jpeg', 0.9);
  };

  return (
    <div className="fixed inset-0 bg-black flex flex-col" style={{ fontFamily: 'Inter, sans-serif' }} data-testid="effects-mode">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-3">
        <BackButton fallbackHref={`/event/${slug}/guest`} label="Back" variant="subtle" testId="effects-back-btn" />
        <span className="text-white/60 text-sm font-semibold">{EFFECTS.find(e => e.id === activeEffect)?.emoji} {EFFECTS.find(e => e.id === activeEffect)?.label}</span>
        {recording && <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/80"><div className="w-2 h-2 rounded-full bg-[#15161B] animate-pulse" /><span className="text-white text-xs font-bold">{recordTime}s</span></div>}
      </div>

      {/* Camera + Effect Canvas */}
      <div className="flex-1 relative overflow-hidden">
        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
        <canvas ref={canvasRef} width={360} height={640} className="absolute inset-0 w-full h-full pointer-events-none" />
      </div>

      {/* Effect selector (horizontal scroll) */}
      <div className="absolute bottom-32 left-0 right-0 z-20">
        <div className="flex gap-2 overflow-x-auto px-4 pb-2 no-scrollbar">
          {EFFECTS.map(e => (
            <button key={e.id} onClick={() => setActiveEffect(e.id)}
              className={`flex-shrink-0 flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${activeEffect === e.id ? 'bg-[#15161B]/20 scale-110' : 'bg-black/30 backdrop-blur-sm'}`}
              data-testid={`effect-${e.id}`}>
              <span className="text-xl">{e.emoji}</span>
              <span className="text-white text-[10px] font-medium">{e.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Capture controls */}
      <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-8">
        {/* Photo */}
        <button onClick={takePhoto} disabled={uploading} className="w-14 h-14 rounded-full bg-[#15161B]/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/40 hover:bg-[#15161B]/30 transition-all" data-testid="effects-photo">
          <Camera size={24} weight="bold" className="text-white" />
        </button>
        {/* Record */}
        <button onClick={recording ? stopRec : startRec} disabled={uploading}
          className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${recording ? 'bg-red-500 scale-110' : 'bg-[#15161B]/20 backdrop-blur-sm border-4 border-white/60'}`}
          data-testid="effects-record">
          {recording ? <div className="w-8 h-8 rounded-md bg-[#15161B]" /> : <div className="w-14 h-14 rounded-full bg-red-500" />}
        </button>
        {/* Placeholder for symmetry */}
        <div className="w-14 h-14" />
      </div>

      {uploading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/50">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/50 border-t-transparent" />
        </div>
      )}

      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; } .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
    </div>
  );
};

export default EffectsMode;
